import React, { useState, useRef, useEffect } from 'react';
import ReactDom from 'react-dom';
import { motion, useMotionValue, useDragControls, AnimatePresence, animate } from 'framer-motion';
import styled from 'styled-components';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const Wrapper = styled(motion.div) `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  zindex: 99999999;
  background: rgba(0, 0, 0, 0.5);
`;
const DraggableContainer = styled(motion.div) `
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
`;
const HandleIcon = styled.div(({ type = 'straight' }) => `
  width: 14px;
  height: 10px;
  margin: auto;
  display: flex;
  .bs-drag-handle-ic {
    width: 14px;
    position: relative;
    display: block;
    opacity: 0.2;
    top: -5px;
    transform: scale(0.8);

    &::before,
    &::after {
      border-radius: 10px;
      content: "";
      position: absolute;
      width: 3px;
      height: 15px;
      top: 50%;
      transition: all 0.1s ease-in-out;
      background: #000;
    }
    &::before {
      transform-origin: 50% 50%;
      ${type === 'straight' ? `transform: rotate(90deg);` : ''}
      ${type === 'down' ? `transform: rotate(120deg);` : ''}
      ${type === 'up' ? `transform: rotate(60deg);` : ''}
      
    }
    &::after {
      transform-origin: 50% 50%;
      ${type === 'straight' ? `transform: rotate(90deg);` : ''}
      ${type === 'down' ? `transform: rotate(60deg);` : ''}
      ${type === 'up' ? `transform: rotate(120deg);` : ''}
      right: 0;
    }
  }
`);
const Sheet = styled(motion.div)(({ darkMode }) => `
  width: 100%;
  height: auto;
  background: ${darkMode ? '#282828' : '#fff'};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  ${darkMode ? '* { color: #fff }' : ''};

  ${darkMode
    ? `
  ${HandleIcon}{
    .bs-drag-handle-ic {
      &::before,
      &::after {
          background: #fff;
        }
      }
    }
  `
    : ''}
  
`);
const Content = styled.div `
  display: grid;
  grid-template-rows: auto 1fr auto;
  max-height: 90vh;
  position: relative;
`;
const Body = styled.div `
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;
const BodyContent = styled.div `
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
`;
const Header = styled.div(({ withBorder }) => `
  padding: 17px;
  position: relative;
  ${withBorder
    ? `
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);`
    : ''}
`);
const HeaderContent = styled.div `
  padding-top: 10px;
`;
const Footer = styled.div `
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  padding: 10px;
`;
const Mask = styled(motion.div)(({ debug, cursorMove }) => `
  ${cursorMove ? `cursor: move;` : ''}
  ${debug
    ? `
      background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAgMEgxMFYxMEgwVjBaIiBmaWxsPSIjQkNCQ0JDIi8+CjxwYXRoIGQ9Ik0xMCAxMEgyMFYyMEgxMFYxMFoiIGZpbGw9IiNCQ0JDQkMiLz4KPHBhdGggZD0iTTEwIDBIMjBWMTBIMTBWMFoiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGQ9Ik0wIDEwSDEwVjIwSDBWMTBaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K);
    `
    : ''}
`);
const DragHandle = styled.div.attrs({ className: 'draggable' })(({ debug }) => `
  &.draggable{ cursor: move; }
  ${debug
    ? `
      background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAgMEgxMFYxMEgwVjBaIiBmaWxsPSIjQkNCQ0JDIi8+CjxwYXRoIGQ9Ik0xMCAxMEgyMFYyMEgxMFYxMFoiIGZpbGw9IiNCQ0JDQkMiLz4KPHBhdGggZD0iTTEwIDBIMjBWMTBIMTBWMFoiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGQ9Ik0wIDEwSDEwVjIwSDBWMTBaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K);
      opacity: 0.2;
    `
    : ''}
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`);

const BottomSheetComp = ({ id, content, header, footer, dragHandle, getHandleClose, getHandleCloseCustom, getHandleOpen, getSheetVisibility, onOpen, onClose, darkMode, maskClosable, debug, }) => {
    const sheetY = useMotionValue(0);
    const controls = useDragControls();
    const [open, setOpen] = useState(true);
    const sheetRef = useRef(null);
    const [sheetHeight, setSheetHeight] = useState(0);
    const [arrow, setArrow] = useState('straight');
    const sheetId = id !== null && id !== void 0 ? id : 'default';
    const elementId = `chcepe-bottom-sheet-${sheetId}`;
    const handleClose = () => {
        if (!open)
            return;
        setOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    };
    const handleCloseCustom = (cb) => {
        setOpen(false);
        cb === null || cb === void 0 ? void 0 : cb();
    };
    const handleOpen = () => {
        setOpen(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    };
    getHandleCloseCustom === null || getHandleCloseCustom === void 0 ? void 0 : getHandleCloseCustom(handleCloseCustom);
    getHandleOpen === null || getHandleOpen === void 0 ? void 0 : getHandleOpen(handleOpen);
    getHandleClose === null || getHandleClose === void 0 ? void 0 : getHandleClose(handleClose);
    getSheetVisibility === null || getSheetVisibility === void 0 ? void 0 : getSheetVisibility(open);
    useEffect(() => {
        var _a, _b;
        setSheetHeight((_b = (_a = sheetRef === null || sheetRef === void 0 ? void 0 : sheetRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : 0);
        if (open) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
    }, [sheetRef, open, onOpen]);
    const maxDrag = sheetHeight / 2;
    const removeToDom = () => {
        var _a, _b;
        if (typeof window !== 'undefined') {
            (_b = (_a = document === null || document === void 0 ? void 0 : document.getElementById) === null || _a === void 0 ? void 0 : _a.call(document, elementId)) === null || _b === void 0 ? void 0 : _b.remove();
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(AnimatePresence, { onExitComplete: () => removeToDom() }, open && (React.createElement(Wrapper, { transition: { duration: 0.2 }, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
            React.createElement(DraggableContainer, { id: "draggable-container", style: {
                    y: sheetY,
                }, drag: "y", dragControls: controls, dragConstraints: {
                    top: 0,
                    bottom: sheetHeight,
                }, dragElastic: 0.1, onDrag: (_, info) => {
                    setArrow(maxDrag < info.offset.y ? 'down' : 'up');
                }, onDragStart: (e, info) => {
                    var _a, _b;
                    if (!((_b = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('draggable'))) {
                        // @ts-ignore
                        controls === null || controls === void 0 ? void 0 : controls.componentControls.forEach((entry) => {
                            entry.stop(e, info);
                        });
                    }
                }, onDragEnd: () => {
                    setArrow('straight');
                    if (sheetY.get() < maxDrag) {
                        animate(sheetY, 0, { type: 'spring' });
                    }
                    else {
                        setArrow('down');
                        handleClose();
                    }
                } },
                React.createElement(Mask, Object.assign({ transition: { duration: 0.2 }, initial: { opacity: 0 }, animate: { opacity: 0.2 }, exit: { opacity: 0 }, debug: debug }, (maskClosable
                    ? {
                        onTap: () => handleClose(),
                    }
                    : {}), ((dragHandle === null || dragHandle === void 0 ? void 0 : dragHandle.includes('mask')) ? { className: 'draggable', cursorMove: true } : {}))),
                React.createElement(Sheet, { ref: sheetRef, id: "sheet-content", initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' }, transition: {
                        duration: 0.2,
                    }, darkMode: darkMode },
                    React.createElement(Content, null,
                        React.createElement(Header, { withBorder: Boolean(header) },
                            React.createElement(HandleIcon, { type: arrow },
                                React.createElement("div", { className: "bs-drag-handle-ic" })),
                            header && React.createElement(HeaderContent, null, header),
                            (dragHandle === null || dragHandle === void 0 ? void 0 : dragHandle.includes('header')) && React.createElement(DragHandle, { debug: debug })),
                        React.createElement(Body, null,
                            React.createElement(BodyContent, null, content),
                            (dragHandle === null || dragHandle === void 0 ? void 0 : dragHandle.includes('content')) && React.createElement(DragHandle, { debug: debug })),
                        footer ? (React.createElement(Footer, null,
                            footer,
                            (dragHandle === null || dragHandle === void 0 ? void 0 : dragHandle.includes('footer')) && React.createElement(DragHandle, { debug: debug }))) : (React.createElement("div", null))))))))));
};

const sheetInfo = {
    default: {
        visibility: undefined,
        open: undefined,
        close: undefined,
        closeCustom: undefined,
    },
};
const bottomSheet = {
    create: (_a) => {
        var _b, _c;
        var { dragHandle = ['header', 'mask'], maskClosable = true, id } = _a, rest = __rest(_a, ["dragHandle", "maskClosable", "id"]);
        const sheetId = id !== null && id !== void 0 ? id : 'default';
        const elementId = `chcepe-bottom-sheet-${sheetId}`;
        if (typeof window !== 'undefined') {
            (_c = (_b = document === null || document === void 0 ? void 0 : document.getElementById) === null || _b === void 0 ? void 0 : _b.call(document, elementId)) === null || _c === void 0 ? void 0 : _c.remove();
            const containerDomNode = document.createElement('div');
            containerDomNode.setAttribute('id', elementId);
            document === null || document === void 0 ? void 0 : document.body.appendChild(containerDomNode);
            sheetInfo[sheetId] = {};
            ReactDom.render(React.createElement(BottomSheetComp, Object.assign({ id: id, dragHandle: dragHandle, maskClosable: maskClosable, getHandleCloseCustom: (value) => {
                    sheetInfo[sheetId].closeCustom = value;
                }, getSheetVisibility: (value) => {
                    sheetInfo[sheetId].visibility = value;
                }, getHandleClose: (value) => {
                    sheetInfo[sheetId].close = value;
                }, getHandleOpen: (value) => {
                    sheetInfo[sheetId].open = value;
                } }, rest)), containerDomNode);
        }
    },
    close: (options) => {
        var _a, _b, _c, _d, _e, _f;
        const sheetId = (_a = options === null || options === void 0 ? void 0 : options.id) !== null && _a !== void 0 ? _a : 'default';
        if (!((_b = sheetInfo[sheetId]) === null || _b === void 0 ? void 0 : _b.visibility))
            return;
        if (options === null || options === void 0 ? void 0 : options.callback) {
            (_d = (_c = sheetInfo[sheetId]) === null || _c === void 0 ? void 0 : _c.closeCustom) === null || _d === void 0 ? void 0 : _d.call(_c, options.callback);
        }
        else {
            (_f = (_e = sheetInfo[sheetId]) === null || _e === void 0 ? void 0 : _e.close) === null || _f === void 0 ? void 0 : _f.call(_e);
        }
    },
    isOpen: (id) => {
        const sheetId = id !== null && id !== void 0 ? id : 'default';
        return sheetInfo[sheetId].visibility;
    },
};

export { bottomSheet };
